import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../../services/authService';
import { confirmDialog } from '../../../utils/confirmDialog';
import { notifyError, notifySuccess } from '../../../utils/notify';

const ACCOUNT_TYPES = [
  { value: 'EMPLOYEE', label: 'Employee' },
  { value: 'CONSULTANT_PORTAL', label: 'Consultant Portal User' },
  { value: 'CLIENT_PORTAL', label: 'Client Portal User' },
  { value: 'OTHER', label: 'Other External User' },
];

const emptyForm = () => ({
  name: '',
  email: '',
  mobile: '',
  employeeId: '',
  department: '',
  designation: '',
  accountType: 'EMPLOYEE',
  accountStatus: 'ACTIVE',
  role_codes: [],
  projectAssignments: [],
  sendInvitation: true,
  password: '',
});

const UsersAccessUsers = ({ initialFilter }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(initialFilter === 'locked' ? 'LOCKED' : 'all');
  const [attentionFilter, setAttentionFilter] = useState(['no-projects', 'no-roles'].includes(initialFilter) ? initialFilter : '');
  const [menuId, setMenuId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(initialFilter === 'add');
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [u, r, p] = await Promise.all([
      adminAPI.getUsers(),
      adminAPI.getRoles(),
      adminAPI.getProjects(),
    ]);
    setUsers(u?.data?.data || u?.data || []);
    setRoles((r?.data?.data || r?.data || []).filter((x) => x.status !== 'Inactive'));
    setProjects((p?.data?.data || p?.data || []).filter((x) => x.status === 'Active' || !x.status));
  }, []);

  useEffect(() => { load().catch(() => notifyError('Unable to load users')); }, [load]);
  useEffect(() => {
    if (initialFilter === 'add') setDrawerOpen(true);
    if (initialFilter === 'locked') setStatusFilter('LOCKED');
    if (['no-projects', 'no-roles'].includes(initialFilter)) setAttentionFilter(initialFilter);
  }, [initialFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (q && ![u.name, u.email, u.mobile].some((x) => String(x || '').toLowerCase().includes(q))) return false;
      if (typeFilter !== 'all' && u.accountType !== typeFilter) return false;
      if (statusFilter !== 'all' && u.accountStatus !== statusFilter) return false;
      if (attentionFilter === 'no-roles' && (u.role_codes || []).length > 0) return false;
      if (attentionFilter === 'no-projects' && (u.projects || []).length > 0) return false;
      return true;
    });
  }, [users, search, typeFilter, statusFilter, attentionFilter]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setStep(1);
    setDrawerOpen(true);
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      ...emptyForm(),
      name: u.name || '',
      email: u.email || '',
      mobile: u.mobile || '',
      employeeId: u.employeeId || '',
      department: u.department || '',
      designation: u.designation || '',
      accountType: u.accountType || 'EMPLOYEE',
      accountStatus: u.accountStatus || 'ACTIVE',
      role_codes: u.role_codes || [],
      projectAssignments: (u.assignments || []).map((a) => ({
        id: a.id,
        projectId: a.projectId || a.project?.id,
        responsibility: a.role || 'SITE',
        isPrimary: Boolean(a.isPrimary),
        accessStartDate: a.accessStartDate || '',
        accessEndDate: a.accessEndDate || '',
      })),
      sendInvitation: false,
    });
    setStep(1);
    setDrawerOpen(true);
    setMenuId(null);
  };

  const toggleRole = (code) => {
    setForm((prev) => {
      const set = new Set(prev.role_codes);
      if (set.has(code)) set.delete(code); else set.add(code);
      return { ...prev, role_codes: Array.from(set) };
    });
  };

  const syncProjectAssignments = async (userId) => {
    const desired = form.projectAssignments.filter((pa) => pa.projectId);
    const desiredProjectIds = new Set(desired.map((pa) => Number(pa.projectId)));
    const currentUser = users.find((u) => u.id === userId);
    const existing = currentUser?.assignments || [];

    for (const a of existing) {
      if (!desiredProjectIds.has(Number(a.projectId))) {
        await adminAPI.deleteAssignment(a.id);
      }
    }

    for (const pa of desired) {
      const pid = Number(pa.projectId);
      const match = existing.find((a) => Number(a.projectId) === pid);
      const payload = {
        role: pa.responsibility || 'SITE',
        isPrimary: Boolean(pa.isPrimary),
        accessStartDate: pa.accessStartDate || null,
        accessEndDate: pa.accessEndDate || null,
      };
      if (match) {
        await adminAPI.updateAssignment(match.id, payload);
      } else {
        await adminAPI.createAssignment({
          userId,
          projectId: pid,
          ...payload,
        });
      }
    }
  };

  const save = async ({ invite }) => {
    setSaving(true);
    try {
      if (editingId) {
        await adminAPI.updateUser(editingId, {
          name: form.name,
          email: form.email,
          mobile: form.mobile,
          employeeId: form.employeeId,
          department: form.department,
          designation: form.designation,
          accountType: form.accountType,
          accountStatus: form.accountStatus,
          role_codes: form.role_codes,
          ...(form.password ? { password: form.password } : {}),
        });
        await syncProjectAssignments(editingId);
        if (invite) {
          const res = await adminAPI.inviteUser(editingId, { resend: true });
          if (res?.data?.invitationFailed) {
            notifyError(res.data.reason || res.data.invitationMessage || 'User updated but invitation email failed.');
          } else {
            notifySuccess('User updated and invitation sent');
          }
        } else {
          notifySuccess('User updated');
        }
      } else {
        const res = await adminAPI.provisionUser({
          ...form,
          sendInvitation: invite,
          password: form.password || undefined,
        });
        if (res?.data?.invitationFailed) {
          notifyError(res.data.invitationMessage || 'User created but invitation email failed.');
        } else {
          notifySuccess(invite ? 'User created and invitation sent' : 'User created');
        }
      }
      setDrawerOpen(false);
      await load();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (u, action) => {
    setMenuId(null);
    try {
      if (action === 'invite') {
        const res = await adminAPI.inviteUser(u.id, { resend: true });
        if (res?.data?.invitationFailed) notifyError(res.data.reason || 'Invitation email failed');
        else notifySuccess('Invitation sent');
      } else if (action === 'lock') {
        if (!(await confirmDialog('Lock this account?', 'Lock Account'))) return;
        await adminAPI.lockUser(u.id);
        notifySuccess('Account locked');
      } else if (action === 'unlock') {
        await adminAPI.unlockUser(u.id);
        notifySuccess('Account unlocked');
      } else if (action === 'deactivate') {
        if (!(await confirmDialog('Deactivate this account?', 'Deactivate'))) return;
        await adminAPI.deactivateUser(u.id);
        notifySuccess('Account deactivated');
      } else if (action === 'reactivate') {
        await adminAPI.reactivateUser(u.id);
        notifySuccess('Account reactivated');
      } else if (action === 'delete') {
        if (!(await confirmDialog('Delete this user permanently?', 'Delete User'))) return;
        await adminAPI.deleteUser(u.id);
        notifySuccess('User deleted');
      } else if (action === 'edit') {
        openEdit(u);
        return;
      }
      await load();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Action failed');
    }
  };

  return (
    <div>
      <div className="filter-bar" style={{ marginBottom: 12 }}>
        <input placeholder="Search name / email / mobile" value={search} onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 220 }} />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All account types</option>
          {ACCOUNT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="LOCKED">Locked</option>
        </select>
        <select value={attentionFilter} onChange={(e) => setAttentionFilter(e.target.value)}>
          <option value="">All users</option>
          <option value="no-roles">Without System Roles</option>
          <option value="no-projects">Without Project Access</option>
        </select>
        <button type="button" className="btn-primary" onClick={openCreate}>Add User</button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>User</th><th>Email</th><th>Mobile</th><th>Account Type</th>
              <th>System Roles</th><th>Projects</th><th>Status</th><th>Invitation</th><th>Last Login</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>No users found</td></tr>
            ) : filtered.map((u) => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td>{u.mobile || '—'}</td>
                <td><span className="ua-badge">{ACCOUNT_TYPES.find((t) => t.value === u.accountType)?.label || u.accountType || 'Employee'}</span></td>
                <td>{(u.role_codes || []).join(', ') || '—'}</td>
                <td>{(u.projects || []).map((p) => p.name).join(', ') || '—'}</td>
                <td><span className={`ua-badge ${u.accountStatus === 'ACTIVE' ? 'ua-badge--ok' : 'ua-badge--warn'}`}>{u.accountStatus || 'ACTIVE'}</span></td>
                <td>{u.invitationStatus || '—'}</td>
                <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('en-IN') : '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => openEdit(u)}>Edit</button>
                    <div className="ua-actions-menu">
                      <button type="button" className="btn-secondary btn-sm" onClick={() => setMenuId(menuId === u.id ? null : u.id)}>⋯</button>
                      {menuId === u.id && (
                        <div className="ua-actions-menu__list">
                          <button type="button" onClick={() => runAction(u, 'invite')}>Send / Resend Invitation</button>
                          <button type="button" onClick={() => runAction(u, 'lock')}>Lock Account</button>
                          <button type="button" onClick={() => runAction(u, 'unlock')}>Unlock Account</button>
                          <button type="button" onClick={() => runAction(u, 'deactivate')}>Deactivate</button>
                          <button type="button" onClick={() => runAction(u, 'reactivate')}>Reactivate</button>
                          <button type="button" onClick={() => runAction(u, 'delete')}>Delete User</button>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drawerOpen && (
        <div className="ua-drawer-overlay" onClick={() => setDrawerOpen(false)}>
          <div className="ua-drawer" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>{editingId ? 'Edit User' : 'Add User'}</h2>
            <div className="ua-wizard-steps">
              {[1, 2, 3, 4, 5].map((s) => (
                <span key={s} className={`ua-wizard-step ${step === s ? 'is-active' : ''}`}>
                  {['', 'Basic', 'Roles', 'Projects', 'Onboarding', 'Review'][s]}
                </span>
              ))}
            </div>

            {step === 1 && (
              <div>
                <div className="form-group"><label>Full Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div className="form-group"><label>Mobile</label><input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
                <div className="form-row">
                  <div className="form-group"><label>Employee ID</label><input value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })} /></div>
                  <div className="form-group"><label>Department</label><input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
                </div>
                <div className="form-group"><label>Designation</label><input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
                <div className="form-group">
                  <label>Account Type</label>
                  <select value={form.accountType} onChange={(e) => setForm({ ...form, accountType: e.target.value })}>
                    {ACCOUNT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <p style={{ color: '#64748b', fontSize: 13 }}>Select one or more System Roles.</p>
                {roles.map((r) => (
                  <label key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                    <input type="checkbox" checked={form.role_codes.includes(r.code)} onChange={() => toggleRole(r.code)} />
                    <span>
                      <strong>{r.name}</strong> <span className="ua-badge">{r.code}</span>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{r.description || 'No description'}</div>
                    </span>
                  </label>
                ))}
              </div>
            )}

            {step === 3 && (
              <div>
                <p style={{ color: '#64748b', fontSize: 13 }}>Assign Project Access. Responsibility is not a System Role.</p>
                {projects.map((p) => {
                  const existing = form.projectAssignments.find((x) => String(x.projectId) === String(p.id));
                  return (
                    <div key={p.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={Boolean(existing)}
                          onChange={(e) => {
                            setForm((prev) => {
                              const rest = prev.projectAssignments.filter((x) => String(x.projectId) !== String(p.id));
                              if (!e.target.checked) return { ...prev, projectAssignments: rest };
                              return { ...prev, projectAssignments: [...rest, { projectId: p.id, responsibility: 'SITE', isPrimary: false }] };
                            });
                          }}
                        />
                        <strong>{p.name}</strong>
                      </label>
                      {existing && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                          <select
                            value={existing.responsibility}
                            onChange={(e) => setForm((prev) => ({
                              ...prev,
                              projectAssignments: prev.projectAssignments.map((x) => String(x.projectId) === String(p.id) ? { ...x, responsibility: e.target.value } : x),
                            }))}
                          >
                            <option value="SITE">SITE</option>
                            <option value="VIEWER">VIEWER</option>
                            <option value="PM">PM</option>
                            <option value="HO">HO</option>
                          </select>
                          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(existing.isPrimary)}
                              onChange={(e) => setForm((prev) => ({
                                ...prev,
                                projectAssignments: prev.projectAssignments.map((x) => ({
                                  ...x,
                                  isPrimary: String(x.projectId) === String(p.id) ? e.target.checked : false,
                                })),
                              }))}
                            />
                            Primary project
                          </label>
                          <span style={{ fontSize: 12, color: '#64748b' }}>Project Responsibility</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {step === 4 && (
              <div>
                {!editingId && (
                  <div className="form-group">
                    <label>Temporary password (optional if sending invitation)</label>
                    <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  </div>
                )}
                {editingId && (
                  <div className="form-group">
                    <label>New password (optional)</label>
                    <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                  </div>
                )}
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="checkbox" checked={form.sendInvitation} onChange={(e) => setForm({ ...form, sendInvitation: e.target.checked })} />
                  Send invitation email after save
                </label>
              </div>
            )}

            {step === 5 && (
              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                <p><strong>Name:</strong> {form.name}</p>
                <p><strong>Email:</strong> {form.email}</p>
                <p><strong>Account Type:</strong> {form.accountType}</p>
                <p><strong>System Roles:</strong> {form.role_codes.join(', ') || '—'}</p>
                <p><strong>Projects:</strong> {form.projectAssignments.map((x) => {
                  const name = projects.find((p) => String(p.id) === String(x.projectId))?.name;
                  return `${name || x.projectId} (${x.responsibility}${x.isPrimary ? ', primary' : ''})`;
                }).join('; ') || '—'}</p>
                <p><strong>Invitation:</strong> {form.sendInvitation ? 'Send invitation' : 'Do not send'}</p>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
              <button type="button" className="btn-secondary" onClick={() => setDrawerOpen(false)}>Cancel</button>
              {step > 1 && <button type="button" className="btn-secondary" onClick={() => setStep((s) => s - 1)}>Back</button>}
              {step < 5 && <button type="button" className="btn-primary" onClick={() => setStep((s) => s + 1)}>Next</button>}
              {step === 5 && (
                <>
                  {!editingId && (
                    <button type="button" className="btn-secondary" disabled={saving} onClick={() => save({ invite: false })}>
                      Create Without Invitation
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={saving}
                    onClick={() => save({ invite: Boolean(form.sendInvitation) })}
                  >
                    {editingId
                      ? (form.sendInvitation ? 'Save User & Send Invitation' : 'Save User')
                      : (form.sendInvitation ? 'Create User & Send Invitation' : 'Create User')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersAccessUsers;
